import { useStore } from '@nanostores/react';
import { memo, useEffect, useMemo, useRef } from 'react';
import { Panel, PanelGroup, PanelResizeHandle, type ImperativePanelHandle } from 'react-resizable-panels';
import { FileBreadcrumb } from './FileBreadcrumb';
import { FileTreePanel } from './FileTreePanel';
import { BoltTerminal, ShellTerminal } from './terminal/WebContainerTerminal';
import {
  CodeMirrorEditor,
  type EditorDocument,
  type EditorSettings,
} from '~/components/editor/codemirror/CodeMirrorEditor';
import { Button } from '~/components/ui/Button';
import { LegacyIconButton } from '~/components/ui/LegacyIconButton';
import { PanelHeader } from '~/components/ui/PanelHeader';
import { PanelHeaderButton } from '~/components/ui/PanelHeaderButton';
import { TextShimmer } from '~/components/ui/TextShimmer';
import type { FileChangeCallback } from '~/components/workbench/FileContextMenu';
import { shortcutEventEmitter } from '~/lib/hooks';
import { chatStore } from '~/lib/stores/chat';
import type { FileMap } from '~/lib/stores/files';
import { BOLT_TERMINAL, DEPLOY_TERMINAL } from '~/lib/stores/terminal';
import { themeStore } from '~/lib/stores/theme';
import { versionHistoryStore } from '~/lib/stores/version-history/version-history';
import { workbenchStore } from '~/lib/stores/workbench';
import { classNames } from '~/utils/classNames';
import { WORK_DIR } from '~/utils/constants';
import { renderLogger } from '~/utils/logger';

interface EditorPanelProps {
  files?: FileMap;
  unsavedFiles?: Set<string>;
  editorDocument?: EditorDocument;
  selectedFile?: string | undefined;
  visible: boolean;
  onFileSelect?: (value?: string) => void;
  onFileSave?: () => void;
  onFileReset?: () => void;
  onFileChange?: FileChangeCallback;
}

// the max terminals is actually 3, but we want an additional terminal for Bolt
const MAX_TERMINALS = 4;

const DEFAULT_TERMINAL_SIZE = 25;
const DEFAULT_EDITOR_SIZE = 100 - DEFAULT_TERMINAL_SIZE;

const editorSettings: EditorSettings = { tabSize: 2 };

export const EditorPanel = memo(
  ({
    files,
    unsavedFiles,
    editorDocument,
    selectedFile,
    visible,
    onFileSelect,
    onFileSave,
    onFileReset,
    onFileChange,
  }: EditorPanelProps) => {
    renderLogger.trace('EditorPanel');

    const theme = useStore(themeStore);
    const showTerminal = useStore(workbenchStore.showTerminal);
    const pendingFixes = useStore(workbenchStore.pendingFixes);
    const isReadOnly = useStore(chatStore.isReadOnly);

    const hasFiles = !files ? false : Object.keys(files).length > 0;

    const isSelectedFileBeingFixed = selectedFile && workbenchStore.isFixing.get() && pendingFixes[selectedFile];

    const terminalPanelRef = useRef<ImperativePanelHandle>(null);
    const terminalToggledByShortcut = useRef(false);

    const activeFileSegments = useMemo(() => {
      if (!editorDocument) {
        return undefined;
      }

      return editorDocument.filePath.split('/');
    }, [editorDocument]);

    const activeFileUnsaved = useMemo(() => {
      return editorDocument !== undefined && unsavedFiles?.has(editorDocument.filePath);
    }, [editorDocument, unsavedFiles]);

    useEffect(() => {
      const unsubscribeFromEventEmitter = shortcutEventEmitter.on('toggleTerminal', () => {
        terminalToggledByShortcut.current = true;
      });

      return () => {
        unsubscribeFromEventEmitter();
      };
    }, []);

    useEffect(() => {
      const { current: terminal } = terminalPanelRef;

      if (!terminal) {
        return;
      }

      const isCollapsed = terminal.isCollapsed();

      if (!showTerminal && !isCollapsed) {
        terminal.collapse();
      } else if (showTerminal && isCollapsed) {
        terminal.resize(DEFAULT_TERMINAL_SIZE);
      }

      terminalToggledByShortcut.current = false;
    }, [showTerminal]);

    return (
      <div className="flex h-full relative">
        {isReadOnly && (
          <div className="absolute inset-0 bg-bolt-ds-surfaceOne/90 backdrop-blur z-1 flex items-center flex-col gap-4 justify-center">
            <div className="text-bolt-elements-textSecondary">Code changes are locked in preview mode</div>

            <Button size="md" variant="secondary" onClick={() => versionHistoryStore.cancelPreview()}>
              Exit preview
            </Button>
          </div>
        )}
        <PanelGroup direction="vertical">
          <Panel defaultSize={showTerminal ? DEFAULT_EDITOR_SIZE : 100} minSize={20}>
            <PanelGroup direction="horizontal">
              <Panel defaultSize={23} minSize={10} collapsible>
                <FileTreePanel
                  files={files}
                  onFileSelect={onFileSelect}
                  onFileChange={onFileChange}
                  rootFolder={WORK_DIR}
                  selectedFile={selectedFile}
                  unsavedFiles={unsavedFiles}
                />
              </Panel>
              <PanelResizeHandle className={!visible ? 'hidden' : ''} />
              <Panel className="flex flex-col" defaultSize={77} minSize={20}>
                <PanelHeader className="overflow-x-auto">
                  {activeFileSegments?.length && (
                    <div className="flex items-center flex-1 text-sm">
                      <FileBreadcrumb pathSegments={activeFileSegments} files={files} onFileSelect={onFileSelect} />
                      {isSelectedFileBeingFixed && (
                        <div className="flex items-center gap-1 text-[11px] px-1.5 bg-alpha-accent-10 text-bolt-elements-item-contentAccent ml-auto rounded-md -mr-1.5">
                          <div className="i-svg-spinners:90-ring-with-bg text-xs" /> Fixing file
                        </div>
                      )}
                      {activeFileUnsaved && (
                        <div className="flex gap-1 ml-auto -mr-1.5">
                          <PanelHeaderButton onClick={onFileSave}>
                            <div className="i-ph:floppy-disk-duotone" />
                            Save
                          </PanelHeaderButton>
                          <PanelHeaderButton onClick={onFileReset}>
                            <div className="i-ph:clock-counter-clockwise-duotone" />
                            Reset
                          </PanelHeaderButton>
                        </div>
                      )}
                    </div>
                  )}
                </PanelHeader>
                <div className="relative h-full flex-1 overflow-hidden">
                  <CodeMirrorEditor theme={theme} settings={editorSettings} doc={editorDocument} />
                  {!hasFiles && <LoadingOverlay />}
                </div>
              </Panel>
            </PanelGroup>
          </Panel>
          <PanelResizeHandle className={!visible ? 'hidden' : ''} />
          <Panel
            ref={terminalPanelRef}
            defaultSize={showTerminal ? DEFAULT_TERMINAL_SIZE : 0}
            minSize={10}
            collapsible
            className="min-h-[calc(var(--panel-header-height)-1px)]"
            onExpand={() => {
              if (!terminalToggledByShortcut.current) {
                workbenchStore.toggleTerminal(true);
              }
            }}
            onCollapse={() => {
              if (!terminalToggledByShortcut.current) {
                workbenchStore.toggleTerminal(false);
              }
            }}
          >
            <TerminalPanel isClosed={!showTerminal} onToggle={(value) => workbenchStore.toggleTerminal(value)} />
          </Panel>
        </PanelGroup>
      </div>
    );
  },
);

const LoadingOverlay = memo(() => {
  return (
    <div className="absolute inset-0 bg-[var(--cm-backgroundColor)] flex items-center justify-center">
      <TextShimmer
        className="[--base-color:var(--bolt-elements-textTertiary)] dark:[--base-color:var(--bolt-elements-textSecondary)] [--base-gradient-color:var(--bolt-elements-textPrimary)]"
        duration={1.5}
        spread={2}
        length={120}
      >
        Setting up your project…
      </TextShimmer>
    </div>
  );
});

interface TerminalPanelProps {
  isClosed: boolean;
  onToggle?: (value?: boolean) => void;
}

function TerminalPanel({ isClosed, onToggle }: TerminalPanelProps) {
  const terminals = useStore(workbenchStore.terminals);
  const activeTerminal = useStore(workbenchStore.activeTerminal);

  const setActiveTerminal = workbenchStore.activeTerminal.set;

  const terminalCount = terminals.length;
  const closeable = terminalCount > 1;

  function addTerminal() {
    const terminalId = workbenchStore.addTerminal();

    setActiveTerminal(terminalId);
  }

  function handleTerminalOpen(terminalId: number) {
    setActiveTerminal(terminalId);

    if (isClosed) {
      onToggle?.(true);
    }
  }

  function tryCloseTerminal(id: number) {
    if (!closeable) {
      return false;
    }

    workbenchStore.removeTerminal(id);

    return true;
  }

  return (
    <div className="h-full">
      <div className="bg-bolt-elements-terminals-background h-full flex flex-col">
        <div
          className="flex items-center bg-bolt-elements-background-depth-1 border-y border-bolt-elements-borderColor gap-1.5 min-h-[var(--panel-header-height)] p-2"
          role="tablist"
        >
          {terminals.map((terminalId, index) => {
            const isActive = activeTerminal === terminalId;

            const icon =
              terminalId === BOLT_TERMINAL
                ? 'i-ph:lightning-duotone'
                : terminalId === DEPLOY_TERMINAL
                  ? 'i-ph:rocket-launch'
                  : 'i-ph:terminal-window-duotone';

            const title =
              terminalId === BOLT_TERMINAL ? 'Bolt' : terminalId === DEPLOY_TERMINAL ? 'Publish Output' : 'Terminal';

            return (
              <button
                key={index}
                className={classNames(
                  'flex items-center text-sm cursor-pointer gap-1.5 px-3 py-2 h-full whitespace-nowrap rounded-full',
                  {
                    'bg-bolt-elements-terminals-buttonBackground text-bolt-elements-textPrimary': isActive,
                    'bg-bolt-elements-background-depth-2 text-bolt-elements-textSecondary hover:bg-bolt-elements-terminals-buttonBackground':
                      !isActive,
                  },
                )}
                onClick={() => handleTerminalOpen(terminalId)}
                id={`bolt-terminal-tab-${index}`}
                role="tab"
                aria-selected={isActive}
                aria-controls={`bolt-terminal-tapbanel-${index}`}
              >
                <div className={`${icon} text-lg`} />
                {title}{' '}
                {terminalId !== BOLT_TERMINAL && terminalId !== DEPLOY_TERMINAL && terminalCount > 3 && (
                  <span>{index - 1}</span>
                )}
              </button>
            );
          })}

          {terminalCount < MAX_TERMINALS && <LegacyIconButton icon="i-ph:plus" size="md" onClick={addTerminal} />}

          <LegacyIconButton
            className="ml-auto"
            icon={isClosed ? 'i-ph:caret-up' : 'i-ph:caret-down'}
            title={isClosed ? 'Open' : 'Close'}
            size="md"
            onClick={() => onToggle?.()}
          />
        </div>

        {terminals.map((terminalId, index) => {
          const isActive = activeTerminal === terminalId;

          if (terminalId === BOLT_TERMINAL || terminalId === DEPLOY_TERMINAL) {
            return (
              <BoltTerminal
                driver={terminalId === BOLT_TERMINAL ? workbenchStore.terminalDriver : workbenchStore.deployDriver}
                key={terminalId}
                className={classNames('h-full overflow-hidden', {
                  hidden: !isActive,
                })}
                role="tabpanel"
                id={`bolt-terminal-tapbanel-${index}`}
                aria-labelledby={`bolt-terminal-tab-${index}`}
              />
            );
          }

          return (
            <ShellTerminal
              key={terminalId}
              className={classNames('h-full overflow-hidden', {
                hidden: !isActive,
              })}
              onTryExit={() => tryCloseTerminal(terminalId)}
              role="tabpanel"
              id={`bolt-terminal-tapbanel-${index}`}
              aria-labelledby={`bolt-terminal-tab-${index}`}
            />
          );
        })}
      </div>
    </div>
  );
}
